import { create } from "zustand";
import { AppliedFilter } from "./components/InventorySearch/Filters";
import { Timestamp } from "firebase/firestore";
import { Product } from "./common/models/product";

export type SearchHistoryItem = {
  // Firestore document id (present when loaded from users/{uid}/user-search-history)
  id?: string;
  query: string;
  urlQueryString: string;
  // This is a firebase timestamp object
  timestamp: Timestamp;
  numResults?: number;
  minMismatchGrade?: number;
  improvementNotificationShown?: boolean;
  appliedFilters?: AppliedFilter[];
  lastCheckedForNewResults?: Timestamp;
};

type SearchHistoryStore = {
  totalUserSearchHistory: SearchHistoryItem[];
  setTotalUserSearchHistory: (history: SearchHistoryItem[]) => void;
  sessionSearchQueryHistory: string[];
  addSessionSearchQuery: (query: string) => void;
};

export const useSearchHistory = create<SearchHistoryStore>((set) => ({
  totalUserSearchHistory: [] as SearchHistoryItem[],
  setTotalUserSearchHistory: (history) =>
    set({ totalUserSearchHistory: history }),
  sessionSearchQueryHistory: [] as string[],
  addSessionSearchQuery: (query) =>
    set((state) => ({
      sessionSearchQueryHistory: [...state.sessionSearchQueryHistory, query],
    })),
}));

export type SearchResultsCache = {
  searchQuery: string;
  structuredQuery: any;
  results: Product[];
  totalResults: number;
  currentPage: number;
  appliedFilters: AppliedFilter[];
};

type SearchResultsCacheStore = {
  searchResultsCache: SearchResultsCache | null;
  setSearchResultsCache: (cache: SearchResultsCache) => void;
};

export const useSearchResultsCache = create<SearchResultsCacheStore>((set) => ({
  searchResultsCache: null,
  setSearchResultsCache: (cache) => set({ searchResultsCache: cache }),
}));

type ShapesStore = {
  shapes: string[];
  setShapes: (shapes: string[]) => void;
};

export const useShapes = create<ShapesStore>((set) => ({
  shapes: [] as string[],
  setShapes: (shapes) => set({ shapes }),
}));

type RfqQueriesStore = {
  rfqQueries: string[];
  setRfqQueries: (queries: string[]) => void;
};

export const useRfqQueries = create<RfqQueriesStore>((set) => ({
  rfqQueries: [] as string[],
  setRfqQueries: (queries) => set({ rfqQueries: queries }),
}));

type DimensionsUnitStore = {
  dimensionsUnit: string;
  setDimensionsUnit: (unit: string) => void;
};

export const useDimensionsUnit = create<DimensionsUnitStore>((set) => ({
  dimensionsUnit: "in", // Default to inches
  setDimensionsUnit: (unit) => set({ dimensionsUnit: unit }),
}));

type ExperimentFlagsStore = {
  dummySearchExperimentVariant: string;
  setDummySearchExperimentVariant: (flag: string) => void;
};

export const useExperimentFlags = create<ExperimentFlagsStore>((set) => ({
  dummySearchExperimentVariant: "control",
  setDummySearchExperimentVariant: (flag) =>
    set({ dummySearchExperimentVariant: flag }),
}));

type MiscSettingsStore = {
  showSearchFeedbackElements: boolean;
  setShowSearchFeedbackElements: (show: boolean) => void;
};

export const useMiscSettings = create<MiscSettingsStore>((set) => ({
  showSearchFeedbackElements: false,
  setShowSearchFeedbackElements: (show) =>
    set({ showSearchFeedbackElements: show }),
}));

type SidebarStore = {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
};

export const useSidebar = create<SidebarStore>((set) => ({
  sidebarOpen: false,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
}));

/**
 * Whether a Kono super user has unlocked the price-provenance tools for this
 * session, and until when.
 *
 * Unlocking costs a password re-entry, so it is held here rather than per page:
 * the toggle lives in the app shell while the tools it reveals are on the
 * product page. `expiresAt` mirrors the server's own window — the API rejects a
 * stale re-authentication regardless, and keeping the deadline here means the UI
 * stops offering an edit at the same moment the API starts refusing it.
 */
type SuperUserModeStore = {
  active: boolean;
  expiresAt: number | null;
  activate: (expiresAt: number) => void;
  deactivate: () => void;
};

export const useSuperUserMode = create<SuperUserModeStore>((set) => ({
  active: false,
  expiresAt: null,
  activate: (expiresAt) => set({ active: true, expiresAt }),
  deactivate: () => set({ active: false, expiresAt: null }),
}));

// Shared control for the "Upload RFQ" and "Need Help" modals so both the
// search-page buttons and the global exit-intent trigger drive a single
// instance. `helpNotice` shows a highlighted banner (e.g. the exit-intent
// reassurance), `helpStartWithHuman` opens the contact-a-human form, and
// `helpInitialQuestion` is asked automatically on open (used when a search
// returns nothing, so the buyer gets an answer instead of a dead end).
type HelpAndRfqStore = {
  helpOpen: boolean;
  rfqOpen: boolean;
  helpNotice: string | null;
  helpStartWithHuman: boolean;
  helpInitialQuestion: string | null;
  openHelp: (options?: {
    notice?: string;
    startWithHuman?: boolean;
    initialQuestion?: string;
  }) => void;
  openRfq: () => void;
  closeHelp: () => void;
  closeRfq: () => void;
};

export const useHelpAndRfq = create<HelpAndRfqStore>((set) => ({
  helpOpen: false,
  rfqOpen: false,
  helpNotice: null,
  helpStartWithHuman: false,
  helpInitialQuestion: null,
  openHelp: (options) =>
    set({
      helpOpen: true,
      helpNotice: options?.notice ?? null,
      helpStartWithHuman: options?.startWithHuman ?? false,
      helpInitialQuestion: options?.initialQuestion ?? null,
    }),
  openRfq: () => set({ rfqOpen: true }),
  closeHelp: () =>
    set({
      helpOpen: false,
      helpNotice: null,
      helpStartWithHuman: false,
      helpInitialQuestion: null,
    }),
  closeRfq: () => set({ rfqOpen: false }),
}));
